import * as KendoChart from '@progress/kendo-react-charts'
import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import bodybuilder from 'bodybuilder'
import { LoadSegmentCodes } from 'components/common/load-partner-codes/component'
import { DropdownComponent } from 'components/dropdown/component'
import { HeaderComponent } from 'components/header/component'
import { AggregateIntervalOptions, DateRangesAsDropdownOptions, getMessageSendDateFilterField, SuspectFilterDropdownOptions
} from 'elasticsearch/constants'
import { AggregationType, DateRanges, FilterType, IndexName, toFilter } from 'elasticsearch/constants'
import { ResponseAggregate, ResponsePayload } from 'elasticsearch/types'
import {
  ElasticsearchComponent,
  ElasticsearchProps,
} from 'generic/elasticSearchComponent'
import * as GenericRedux from 'generic/genericRedux'
import * as React from 'react'
import { Button } from 'reactstrap'
import { DEFAULT_COMMUNITY_PARTNER_CODES } from 'shared_server_client/constants'
import * as Constants from './constants'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'
import { ConnectedComponentClass } from "react-redux"
import {Fields} from "../../shared/modals"
import * as Utils from './utils'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { ComponentType } from 'react'
import { PDFReportHeader } from './PDFReportHeader'

const initialState = {
  selectedDateRange: DateRangesAsDropdownOptions[4],
  selectedInterval: AggregateIntervalOptions[0],
  selectedSegmentCode: DEFAULT_COMMUNITY_PARTNER_CODES.ALL,
}

export class InteractionInsightsComponent extends RasaAnalyticsComponent<any, any> {
  constructor(props) {
    super(props, initialState)
  }

  public componentDidMount() {
    super.componentDidMount()
    this.minCustomDateRange()
  }

  public render() {
    return(
      <div className="analytics-component">
        <HeaderComponent
          title={'ANALYTICS'}
          subTitle={'Interaction Insights'}
          description={[
            'Shows when your contacts are most active throughout the day and week.',
            'Helps optimize send times for better engagement rates.',
            'Identifies peak interaction periods for your audience.',
          ]}
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DropdownComponent data={DateRangesAsDropdownOptions}
                                selected={this.state.selectedDateRange.key}
                                onChange={this.dateChanged}/>
          </div>
          <div className="dropdown-inline">
            <DropdownComponent data={SuspectFilterDropdownOptions}
                               selected={this.state.selectedSuspectClick.key}
                               onChange={this.suspectedClickChanged}/>
          </div>
        </div>
        <div className="dropdown-segment-code">
        <LoadSegmentCodes segmentCode={this.state.selectedSegmentCode} setSegmentCode={this.segmentCodeChanged}
          hideLabel={true}></LoadSegmentCodes>
        </div>
        <div className="date-range-picker">
        {this.state.selectedDateRange.key === '7' ?
        <DateRangePicker min={this.state.minCustomDateRange}
                          max ={new Date()}
                          onChange={this.createCustomDate} /> : null}
        </div>
        {this.state.isFilterLoaded &&
        <AnalyticsInteractionInsightsComponent
          dateRange={this.state.selectedDateRange.value}
          dateRangeLabel={this.state.selectedDateRange.description}
          timezone={this.state.timezone}
          useTimezoneForAnalytics={this.state.useTimezoneForAnalytics}
          selectedSegmentCode={this.state.selectedSegmentCode}
          suspectClick={this.state.selectedSuspectClick.value}
        />}
      </div>
    )
  }
}

interface HourlyData {
  hour: number,
  dayOfWeek: string,
  interactions: number,
  opens: number,
  clicks: number,
  interactionRate: number,
}

interface DayOfWeekData {
  dayOfWeek: string,
  dayOfWeekValue?: number,
  totalInteractions: number,
  avgInteractionRate: number,
}

interface InteractionTimeData {
  hourlyData: HourlyData[],
  dayOfWeekData: DayOfWeekData[],
  peakHour: string,
  peakDay: string,
}

interface InteractionTimeProps extends ElasticsearchProps<InteractionTimeData> {
  dateRange: string,
  selectedSegmentCode: string,
  suspectClick?: any
  timezone?: string,
  useTimezoneForAnalytics?: any,
  dateRangeLabel?: string,
}

interface InteractionTimeState {
  loaded: boolean,
}

const HOUR_AGGREGATION: string = 'hour'
const DAY_AGGREGATION: string = 'day_of_week'
const EVENT_AGGREGATION: string = 'event'

class InteractionInsightsClass extends ElasticsearchComponent<InteractionTimeData, InteractionTimeProps, InteractionTimeState> {
  private xlsxExport: any = null
  private pdfExportRef: React.RefObject<HTMLDivElement> = React.createRef()

  constructor(p: InteractionTimeProps) {
    super(p, IndexName.EVENTS)
    this.state = {
      loaded: false,
    }
    this.reportName = Constants.REPORT_NAMES.INTERACTION_INSIGHTS
  }

  public parseResponse(payload: ResponsePayload): Promise<InteractionTimeData> {
    this.setState({ loaded: true })
    
    const hourlyAggregations = payload.aggregations[HOUR_AGGREGATION].buckets
    const dayAggregations = payload.aggregations[DAY_AGGREGATION].buckets

    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    
    const hourlyData: HourlyData[] = hourlyAggregations.map((aggregation: ResponseAggregate) => {
      const hour = parseInt(aggregation.key.toString(), 10)
      const opens = this.getAggregation(aggregation.child, 'open')
      const clicks = this.getAggregation(aggregation.child, 'click')
      
      const openCount = opens ? opens.doc_count : 0
      const clickCount = clicks ? clicks.doc_count : 0
      const totalInteractions = openCount + clickCount

      return {
        hour,
        dayOfWeek: '',
        interactions: totalInteractions,
        opens: openCount,
        clicks: clickCount,
        interactionRate: totalInteractions,
      }
    }).sort((a, b) => a.hour - b.hour)

    const dayOfWeekData: DayOfWeekData[] = dayAggregations.map((aggregation: ResponseAggregate) => {
      const opens = this.getAggregation(aggregation.child, 'open')
      const clicks = this.getAggregation(aggregation.child, 'click')
      
      const openCount = opens ? opens.doc_count : 0
      const clickCount = clicks ? clicks.doc_count : 0
      const totalInteractions = openCount + clickCount
      
      const dayOfWeekValue = parseInt(aggregation.key.toString(), 10)
      const dayOfWeek = dayNames[dayOfWeekValue - 1] || `Day ${aggregation.key}` // Java DOW: 1=Monday, 2=Tuesday, etc.

      return {
        dayOfWeek,
        dayOfWeekValue,
        totalInteractions,
        avgInteractionRate: totalInteractions,
      }
    }).sort((a, b) => a.dayOfWeekValue - b.dayOfWeekValue)

    const peakHourData = hourlyData.reduce((prev, current) => 
      (prev.interactions > current.interactions) ? prev : current
    )
    const peakDayData = dayOfWeekData.reduce((prev, current) => 
      (prev.totalInteractions > current.totalInteractions) ? prev : current
    )

    return Promise.resolve({
      hourlyData,
      dayOfWeekData,
      peakHour: `${peakHourData.hour}:00`,
      peakDay: peakDayData.dayOfWeek,
    })
  }

  public componentDidUpdate(oldProps: InteractionTimeProps) {
    if (this.props.dateRange !== oldProps.dateRange || 
        this.props.suspectClick !== oldProps.suspectClick ||
        this.props.selectedSegmentCode !== oldProps.selectedSegmentCode) {
      this.search()
    }
  }

  public searchPayload(): any {
    const search = bodybuilder().size(0)
       .filter(FilterType.range, getMessageSendDateFilterField(this.props.dateRange, this.props.useTimezoneForAnalytics), toFilter(this.props.dateRange || DateRanges.PastMonth, this.props.timezone, this.props.useTimezoneForAnalytics))

    if (this.props.selectedSegmentCode === DEFAULT_COMMUNITY_PARTNER_CODES.NO_CODE) {
      search.notFilter(FilterType.exists, 'community_partner_code.keyword')
    } else if (this.props.selectedSegmentCode !== DEFAULT_COMMUNITY_PARTNER_CODES.ALL) {
      search.filter(FilterType.term, 'community_partner_code.keyword', this.props.selectedSegmentCode)
    }
    
    if (this.props.suspectClick && Utils.isRealClickSelected(this.props.suspectClick)) {
      search.addFilter(FilterType.term, 'suspect_click', 0)
    }

    search.filter(FilterType.terms, 'event_name.keyword', ['open', 'click'])

    this.addAggregation(search, {
      type: AggregationType.terms,
      extra: {
        script: {
          source: "doc['event_timestamp'].value.getHour()",
          lang: "painless"
        },
        size: 24,
        order: {
          "_key": "asc"
        }
      },
      name: HOUR_AGGREGATION,
      child: {
        type: AggregationType.terms,
        field: 'event_name.keyword',
        name: EVENT_AGGREGATION,
      },
    } as any)
    
    return this.addAggregation(search, {
      type: AggregationType.terms,
      extra: {
        script: {
          source: "doc['event_timestamp'].value.getDayOfWeek()",
          lang: "painless"
        },
        size: 7,
        order: {
          "_key": "asc"
        }
      },
      name: DAY_AGGREGATION,
      child: {
        type: AggregationType.terms,
        field: 'event_name.keyword',
        name: EVENT_AGGREGATION,
      },
    } as any).build()
  }

  public exportToPDF = async () => {
    if (!this.pdfExportRef.current) return

    try {
      const element = this.pdfExportRef.current
      element.classList.add('pdf-export-mode')
      await new Promise(resolve => setTimeout(resolve, 100))

      const canvas = await html2canvas(element, {
        height: element.scrollHeight,
        width: element.scrollWidth,
        useCORS: true,
        scale: 2
      })

      element.classList.remove('pdf-export-mode')

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('l', 'mm', 'a4')

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = 10

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
      pdf.save('interaction-insights-report.pdf')
    } catch (error) {
      if (this.pdfExportRef.current) {
        this.pdfExportRef.current.classList.remove('pdf-export-mode')
      }
    }
  }

  private formatHourLabel = (hour: number): string => {
    if (hour === 0 || hour === 24) return '12 AM'
    if (hour === 12) return '12 PM'
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`
  }


  public render = () => <div className="analytics">
    <div className="interaction-insights" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
      <h4>Interaction Insights</h4>
      {this.props.results && (
        <div style={{ display: 'flex', gap: '30px' }}>
          <div>
            <strong>Peak Hour:</strong> {this.props.results.peakHour}
          </div>
          <div>
            <strong>Peak Day:</strong> {this.props.results.peakDay}
          </div>
        </div>
      )}
    </div>

    <div className="interaction-time-chart">
      {this.props.results && this.props.results.hourlyData.length > 0 ?
        <div>
          <div ref={this.pdfExportRef} className="pdf-report-container">
            <PDFReportHeader
              title="Interaction Insights Report"
              dateRange={this.props.dateRange}
              dateRangeLabel={this.props.dateRangeLabel}
              fields={[
                {
                  label: 'Segment',
                  value: this.props.selectedSegmentCode === 'ALL' ? 'All Segments' : this.props.selectedSegmentCode
                },
                {
                  label: 'Peak Hour',
                  value: this.props.results.peakHour
                },
                {
                  label: 'Peak Day',
                  value: this.props.results.peakDay
                }
              ]}
            />
            
            <div className="pdf-chart-section">
              <h3>Hourly Interaction Distribution</h3>
              <KendoChart.Chart transitions={false} pannable={false} zoomable={false}>
                <KendoChart.ChartLegend position="bottom" orientation="horizontal"/>
                <KendoChart.ChartTooltip
                  render={({point}: KendoChart.TooltipContext) => 
                    <div className="interaction-tooltip">
                      <div>{this.formatHourLabel(point.dataItem.hour)}</div>
                      <div>Interactions: {point.value}</div>
                    </div>
                  }
                />
                <KendoChart.ChartCategoryAxis>
                  <KendoChart.ChartCategoryAxisItem
                    categories={this.props.results.hourlyData.map(item => this.formatHourLabel(item.hour))}
                  />
                </KendoChart.ChartCategoryAxis>
                <KendoChart.ChartValueAxis>
                  <KendoChart.ChartValueAxisItem title={{ text: "Interactions" }} />
                </KendoChart.ChartValueAxis>
                <KendoChart.ChartSeries>
                  <KendoChart.ChartSeriesItem
                    type="column"
                    field="interactions"
                    name="Total Interactions"
                    data={this.props.results.hourlyData}
                    color="#4ecdc4"
                  />
                </KendoChart.ChartSeries>
              </KendoChart.Chart>
            </div>

            <div className="pdf-chart-section">
              <h3>Daily Interaction Distribution</h3>
              <KendoChart.Chart transitions={false} pannable={false} zoomable={false}>
                <KendoChart.ChartLegend position="bottom" orientation="horizontal"/>
                <KendoChart.ChartTooltip
                  render={({point}: KendoChart.TooltipContext) => 
                    <div className="interaction-tooltip">
                      <div>{point.dataItem.dayOfWeek}</div>
                      <div>Interactions: {point.value}</div>
                    </div>
                  }
                />
                <KendoChart.ChartCategoryAxis>
                  <KendoChart.ChartCategoryAxisItem
                    categories={this.props.results.dayOfWeekData.map(item => item.dayOfWeek)}
                  />
                </KendoChart.ChartCategoryAxis>
                <KendoChart.ChartValueAxis>
                  <KendoChart.ChartValueAxisItem title={{ text: "Interactions" }} />
                </KendoChart.ChartValueAxis>
                <KendoChart.ChartSeries>
                  <KendoChart.ChartSeriesItem
                    type="column"
                    field="totalInteractions"
                    name="Total Interactions"
                    data={this.props.results.dayOfWeekData}
                    color="#004f6d"
                  />
                </KendoChart.ChartSeries>
              </KendoChart.Chart>
            </div>

            <div className="pdf-table-section">
              <h3>Detailed Hourly Data</h3>
              <Grid data={this.props.results.hourlyData} className="analytics-counts-grid pdf-table" scrollable="none">
                <GridColumn field="hour" title="Time" cell={({dataItem}) => <td>{this.formatHourLabel(dataItem.hour)}</td>}/>
                <GridColumn field="interactions" title="Total Interactions"/>
                <GridColumn field="opens" title="Opens"/>
                <GridColumn field="clicks" title="Clicks"/>
              </Grid>
            </div>
          </div>

          <div className="interaction-time-exports">
            <Button
              disabled={this.props.results.hourlyData.length < 1}
              onClick={() => this.xlsxExport.save()}
              style={{ marginRight: '10px' }}>
              Export Excel
            </Button>
            <Button
              disabled={this.props.results.hourlyData.length < 1}
              onClick={this.exportToPDF}
              color="secondary">
              Export PDF
            </Button>
            <ExcelExport data={this.props.results.hourlyData}
              fileName="InteractionInsightsReport.xlsx"
              ref={(exporter) => {this.xlsxExport = exporter}}>
              <Grid data={this.props.results.hourlyData} className="analytics-counts-grid" scrollable="none">
                <GridColumn field="hour" title="Time" cell={({dataItem}) => <td>{this.formatHourLabel(dataItem.hour)}</td>}/>
                <GridColumn field="interactions" title="Total Interactions"/>
                <GridColumn field="opens" title="Opens"/>
                <GridColumn field="clicks" title="Clicks"/>
              </Grid>
            </ExcelExport>
          </div>
        </div> :
        <div>
          <p className="no-data-tag">
            {Constants.NO_DATA_COPY}
          </p>
        </div>
      }
    </div>
  </div>
}

export const AnalyticsInteractionInsightsComponent: ConnectedComponentClass<ComponentType<InteractionInsightsClass>, Fields> = GenericRedux.registerNewComponent(
  InteractionInsightsClass, 'analytics_contact_interaction_time', {})